import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CameraHelper, PhotoHelper } from '@/library';
import { PhotoService } from '@/service';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { useToast } from '@/context/ToastContext';
import { usePhotoCache } from '@/context/PhotoCacheContext';
import { ImageMetadata } from '@/api/models';
import { IMAGE_PROVIDER_CONNECTION_ERROR_MESSAGE } from '@/auth/GatewayApiAccessToken';
import { isProductionEnv } from '@/utils';
import DeleteModal from '@/common/DeleteModal';
import PhotoCarousel, { PhotoCarouselItem } from '@/common/PhotoCarousel';
import PhotoDebugErrorModal, { createPhotoDebugError, IPhotoDebugError } from './PhotoDebugErrorModal';
import PhotoStrip from './PhotoStrip';
import { IPhotoSectionPhoto } from './types';
import styles from './PhotoCaptureSection.module.scss';
import wedgeStyles from './WedgePhotoCaptureSection.module.scss';

const emptyImageMetadata: ImageMetadata[] = [];

const getPhotoErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error && error.message === IMAGE_PROVIDER_CONNECTION_ERROR_MESSAGE) {
    return IMAGE_PROVIDER_CONNECTION_ERROR_MESSAGE;
  }

  return fallbackMessage;
};

const createWedgePlaceholder = (slot: PhotoHelper.IWedgePhotoSlot<never>): IPhotoSectionPhoto => ({
  id: slot.type,
  src: '',
  caption: '',
  createdAt: 0,
  name: slot.label,
  size: 0,
  type: slot.type,
  typeLabel: slot.label,
  isPlaceholder: true,
});

interface IWedgePhotoCaptureSectionProps {
  playerId: number;
  ownerRowGuid: string;
  headTypeId: number;
  imageMetadata?: ImageMetadata[] | null;
}

function WedgePhotoCaptureSection({ playerId, ownerRowGuid, headTypeId, imageMetadata }: IWedgePhotoCaptureSectionProps) {
  const isOnline = useOnlineStatus();
  const { showToast } = useToast();
  const {
    getPhotoObjectUrl,
    getPhotoSectionState,
    setPhotoSectionDeletedStorageObjectIds,
    setPhotoSectionDeletedTypedPhotoSlots,
    setPhotoSectionEditedPhotoCaptions,
    setPhotoSectionLocalPhotos,
    setPhotoSectionWedgeTypedPhotos,
  } = usePhotoCache();
  const failedFetchesRef = useRef(new Set<string>());
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [hydratedPhotos, setHydratedPhotos] = useState<IPhotoSectionPhoto[]>([]);
  const [hydratedTypedPhotos, setHydratedTypedPhotos] = useState<Partial<Record<PhotoHelper.WedgePhotoType, IPhotoSectionPhoto>>>({});
  const [activePhotoId, setActivePhotoId] = useState<string | number>();
  const [pageIndex, setPageIndex] = useState(0);
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState<PhotoCarouselItem>();
  const [debugError, setDebugError] = useState<IPhotoDebugError>();
  const photoSectionState = getPhotoSectionState(playerId, ownerRowGuid);
  const localPhotos = photoSectionState.localPhotos as IPhotoSectionPhoto[];
  const wedgeTypedPhotos = photoSectionState.wedgeTypedPhotos as Partial<Record<PhotoHelper.WedgePhotoType, IPhotoSectionPhoto>>;
  const editedPhotoCaptions = photoSectionState.editedPhotoCaptions;
  const deletedStorageObjectIds = photoSectionState.deletedStorageObjectIds;
  const deletedTypedPhotoSlots = photoSectionState.deletedTypedPhotoSlots;
  const ownerImageMetadata = useMemo(() => imageMetadata ?? emptyImageMetadata, [imageMetadata]);
  const typedPhotos = PhotoHelper.wedgePhotoSlots.map((slot) => {
    if (deletedTypedPhotoSlots.has(slot.type)) {
      return createWedgePlaceholder(slot);
    }

    return wedgeTypedPhotos[slot.type] ?? hydratedTypedPhotos[slot.type] ?? createWedgePlaceholder(slot);
  });
  const regularPhotos = PhotoHelper.mergeHydratedPhotosWithLocal(hydratedPhotos, localPhotos);
  const displayPhotos = [...typedPhotos, ...regularPhotos];
  const carouselPhotos = displayPhotos.filter((photo) => !photo.isPlaceholder);
  const visiblePhotos = PhotoHelper.getVisiblePhotoItems(displayPhotos, pageIndex);
  const showPhotoStrip = visiblePhotos.length > 0;

  const showDebugError = (action: string, error: unknown) => {
    if (isProductionEnv) return;
    setDebugError(createPhotoDebugError(action, error));
  };

  const setLocalPhotos = useCallback(
    (updater: SetStateAction<IPhotoSectionPhoto[]>) => setPhotoSectionLocalPhotos(playerId, ownerRowGuid, updater),
    [ownerRowGuid, playerId, setPhotoSectionLocalPhotos],
  );

  const setWedgeTypedPhotos = useCallback(
    (updater: SetStateAction<Partial<Record<PhotoHelper.WedgePhotoType, IPhotoSectionPhoto>>>) => setPhotoSectionWedgeTypedPhotos(playerId, ownerRowGuid, updater),
    [ownerRowGuid, playerId, setPhotoSectionWedgeTypedPhotos],
  );

  const setDeletedStorageObjectIds = useCallback(
    (updater: SetStateAction<Set<number>>) => setPhotoSectionDeletedStorageObjectIds(playerId, ownerRowGuid, updater),
    [ownerRowGuid, playerId, setPhotoSectionDeletedStorageObjectIds],
  );

  const setEditedPhotoCaptions = useCallback(
    (updater: SetStateAction<Record<number, string>>) => setPhotoSectionEditedPhotoCaptions(playerId, ownerRowGuid, updater),
    [ownerRowGuid, playerId, setPhotoSectionEditedPhotoCaptions],
  );

  const setDeletedTypedPhotoSlots = useCallback(
    (updater: SetStateAction<Set<PhotoHelper.WedgePhotoType>>) => setPhotoSectionDeletedTypedPhotoSlots(playerId, ownerRowGuid, updater),
    [ownerRowGuid, playerId, setPhotoSectionDeletedTypedPhotoSlots],
  );

  useEffect(() => {
    failedFetchesRef.current.clear();
  }, [ownerRowGuid, playerId]);

  useEffect(() => {
    let isCancelled = false;

    const hydratePhotos = async () => {
      if (!isOnline || !ownerImageMetadata.length) {
        setHydratedPhotos([]);
        setHydratedTypedPhotos({});
        setIsFetchingImages(false);
        return;
      }

      const nextPhotos: IPhotoSectionPhoto[] = [];
      const nextTypedPhotos: Partial<Record<PhotoHelper.WedgePhotoType, IPhotoSectionPhoto>> = {};
      setIsFetchingImages(true);

      for (const metadata of ownerImageMetadata) {
        if (!metadata.fileName) continue;
        if (deletedStorageObjectIds.has(metadata.storageObjectId)) continue;

        const fetchKey = `${metadata.storageObjectId}:${metadata.fileName}`;
        if (failedFetchesRef.current.has(fetchKey)) continue;

        try {
          const src = await getPhotoObjectUrl(playerId, ownerRowGuid, metadata.fileName);
          const editedCaption = editedPhotoCaptions[metadata.storageObjectId];
          const nextMetadata = editedCaption === undefined ? metadata : { ...metadata, caption: editedCaption };
          const nextPhoto = PhotoHelper.createPhotoFromMetadata(nextMetadata, src);
          const wedgePhotoType = PhotoHelper.getWedgePhotoTypeFromSortId(metadata.sortId);

          if (wedgePhotoType) {
            if (deletedTypedPhotoSlots.has(wedgePhotoType)) continue;

            nextTypedPhotos[wedgePhotoType] = {
              ...nextPhoto,
              type: wedgePhotoType,
              typeLabel: PhotoHelper.getWedgePhotoTypeLabel(wedgePhotoType),
            };
          }

          if (!wedgePhotoType) {
            nextPhotos.push(nextPhoto);
          }
        } catch (error) {
          console.error('Failed to fetch wedge photo:', error);
          showDebugError(`Fetch wedge image: ${metadata.fileName}`, error);
          failedFetchesRef.current.add(fetchKey);
          showToast({ message: getPhotoErrorMessage(error, 'Photo failed to load.'), status: 'error' });
        }
      }

      if (!isCancelled) {
        setHydratedPhotos(nextPhotos);
        setHydratedTypedPhotos(nextTypedPhotos);
        setIsFetchingImages(false);
      }
    };

    hydratePhotos();

    return () => {
      isCancelled = true;
    };
  }, [deletedStorageObjectIds, deletedTypedPhotoSlots, editedPhotoCaptions, getPhotoObjectUrl, isOnline, ownerImageMetadata, ownerRowGuid, playerId, showToast]);

  useEffect(() => {
    setPageIndex((currentPageIndex) => Math.min(currentPageIndex, PhotoHelper.getMaxPhotoPageIndex(displayPhotos.length)));
  }, [displayPhotos.length]);

  const createPhotoFromBlob = (photo: Blob, index: number, uploadTime: number, type?: PhotoHelper.WedgePhotoType) => {
    const src = URL.createObjectURL(photo);
    return PhotoHelper.createLocalPhoto({ photo, index, uploadTime, src, type });
  };

  const updateRegularPhoto = (photoId: string | number, nextPhoto: Partial<IPhotoSectionPhoto>) => {
    setLocalPhotos((currentPhotos) => currentPhotos.map((currentPhoto) => (currentPhoto.id === photoId ? { ...currentPhoto, ...nextPhoto } : currentPhoto)));
  };

  const updateTypedPhoto = (photoId: string | number, nextPhoto: Partial<IPhotoSectionPhoto>) => {
    setWedgeTypedPhotos((currentTypedPhotos) => {
      const typedPhotoEntry = Object.entries(currentTypedPhotos).find(([, photo]) => photo?.id === photoId);
      if (!typedPhotoEntry) return currentTypedPhotos;

      const [type] = typedPhotoEntry as [PhotoHelper.WedgePhotoType, IPhotoSectionPhoto];
      return { ...currentTypedPhotos, [type]: { ...currentTypedPhotos[type]!, ...nextPhoto } };
    });
  };

  const updateHydratedPhoto = (photoId: string | number, nextPhoto: Partial<IPhotoSectionPhoto>) => {
    setHydratedPhotos((currentPhotos) => currentPhotos.map((currentPhoto) => (currentPhoto.id === photoId ? { ...currentPhoto, ...nextPhoto } : currentPhoto)));
    setHydratedTypedPhotos((currentTypedPhotos) => {
      const typedPhotoEntry = Object.entries(currentTypedPhotos).find(([, photo]) => photo?.id === photoId);
      if (!typedPhotoEntry) return currentTypedPhotos;

      const [type] = typedPhotoEntry as [PhotoHelper.WedgePhotoType, IPhotoSectionPhoto];
      return { ...currentTypedPhotos, [type]: { ...currentTypedPhotos[type]!, ...nextPhoto } };
    });
  };

  const updatePhoto = (photoId: string | number, nextPhoto: Partial<IPhotoSectionPhoto>) => {
    updateRegularPhoto(photoId, nextPhoto);
    updateTypedPhoto(photoId, nextPhoto);
  };

  const uploadPhoto = async (photo: IPhotoSectionPhoto) => {
    if (!photo.sourceBlob) return;

    updatePhoto(photo.id, { uploadStatus: 'processing', uploadError: undefined });

    try {
      const uploadResult = await PhotoService.uploadPhoto({
        playerId,
        ownerRowGuid,
        headTypeId,
        photoId: String(photo.id),
        photo: photo.sourceBlob,
        fileName: photo.name,
        caption: photo.caption,
        sortId: PhotoHelper.getWedgePhotoSortId(photo.type),
      });

      updatePhoto(photo.id, {
        uploadStatus: 'complete',
        fileName: uploadResult.fileName,
        contentType: uploadResult.contentType,
        storageObjectId: uploadResult.metadata.storageObjectId,
        entityGuid: uploadResult.metadata.entityGuid,
        metadataRowGuid: uploadResult.metadata.rowGuid,
        sortId: uploadResult.metadata.sortId,
        sourceBlob: undefined,
      });
    } catch (error) {
      console.error('Failed to upload wedge photo:', error);
      showDebugError(`Upload wedge image: ${photo.name}`, error);
      updatePhoto(photo.id, { uploadStatus: 'error', uploadError: 'Upload failed. Please try adding the photo again.' });
      showToast({ message: getPhotoErrorMessage(error, 'Photo upload failed. Please try adding the photo again.'), status: 'error' });
    }
  };

  const handleAddPhoto = async () => {
    const capturedPhoto = await CameraHelper.openCamera();
    if (!capturedPhoto) {
      return;
    }

    const nextPhoto = createPhotoFromBlob(capturedPhoto, 0, Date.now());
    setLocalPhotos((currentPhotos) => [...currentPhotos, nextPhoto]);
    uploadPhoto(nextPhoto);
    setPageIndex((currentPageIndex) => PhotoHelper.getPhotoPageIndexAfterAdd(currentPageIndex));
  };

  const handleAddTypedPhoto = async (type: PhotoHelper.WedgePhotoType) => {
    const capturedPhoto = await CameraHelper.openCamera();
    if (!capturedPhoto) {
      return;
    }

    const typedPhoto = createPhotoFromBlob(capturedPhoto, 0, Date.now(), type);
    const existingTypedPhoto = wedgeTypedPhotos[type];
    if (existingTypedPhoto) URL.revokeObjectURL(existingTypedPhoto.src);
    setDeletedTypedPhotoSlots((currentSlots) => {
      const nextSlots = new Set(currentSlots);
      nextSlots.delete(type);
      return nextSlots;
    });
    setWedgeTypedPhotos((currentTypedPhotos) => PhotoHelper.replaceWedgeTypedPhoto(currentTypedPhotos, type, typedPhoto));
    uploadPhoto(typedPhoto);
  };

  const openCarousel = (photo: IPhotoSectionPhoto) => {
    if (photo.isPlaceholder && photo.type) {
      handleAddTypedPhoto(photo.type);
      return;
    }

    if (photo.isPlaceholder) {
      return;
    }

    setActivePhotoId(photo.id);
    setIsCarouselOpen(true);
  };

  const handleEditPhoto = async (photoToEdit: PhotoCarouselItem, caption: string) => {
    if (!photoToEdit.storageObjectId) {
      showToast({ message: 'Photo caption cannot be saved yet.', status: 'error' });
      return;
    }

    try {
      await PhotoService.updatePhotoCaption({ storageObjectId: photoToEdit.storageObjectId, caption });
      setEditedPhotoCaptions((currentCaptions) => ({ ...currentCaptions, [photoToEdit.storageObjectId!]: caption }));
      updateHydratedPhoto(photoToEdit.id, { caption, label: caption });
      updatePhoto(photoToEdit.id, { caption, label: caption });
    } catch (error) {
      console.error('Failed to update wedge photo caption:', error);
      showToast({ message: 'Photo caption failed to save.', status: 'error' });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingPhoto) return;
    if (!deletingPhoto.storageObjectId) {
      showToast({ message: 'Photo cannot be deleted yet.', status: 'error' });
      return;
    }

    const typedPhotoEntry = Object.entries(wedgeTypedPhotos).find(([, photo]) => photo?.id === deletingPhoto.id);
    const hydratedTypedPhotoEntry = Object.entries(hydratedTypedPhotos).find(([, photo]) => photo?.id === deletingPhoto.id);
    const typedEntry = typedPhotoEntry ?? hydratedTypedPhotoEntry;

    try {
      await PhotoService.deletePhotoMetadata({ storageObjectId: deletingPhoto.storageObjectId });
      const deleteResult = PhotoHelper.deletePhoto(carouselPhotos, deletingPhoto.id, deletingPhoto.id);
      if (localPhotos.some((photo) => photo.src === deletingPhoto.src) || Object.values(wedgeTypedPhotos).some((photo) => photo?.src === deletingPhoto.src)) {
        URL.revokeObjectURL(deletingPhoto.src);
      }

      setDeletedStorageObjectIds((currentIds) => new Set(currentIds).add(deletingPhoto.storageObjectId!));

      if (typedEntry) {
        const [type] = typedEntry as [PhotoHelper.WedgePhotoType, IPhotoSectionPhoto];
        setDeletedTypedPhotoSlots((currentSlots) => new Set(currentSlots).add(type));
        setWedgeTypedPhotos((currentTypedPhotos) => ({ ...currentTypedPhotos, [type]: undefined }));
        setHydratedTypedPhotos((currentTypedPhotos) => ({ ...currentTypedPhotos, [type]: undefined }));
      }

      if (!typedEntry) {
        setLocalPhotos((currentPhotos) => currentPhotos.filter((photo) => photo.id !== deletingPhoto.id));
        setHydratedPhotos((currentPhotos) => currentPhotos.filter((photo) => photo.id !== deletingPhoto.id));
      }

      setDeletingPhoto(undefined);

      if (!deleteResult.photos.length) {
        setIsCarouselOpen(false);
        setActivePhotoId(undefined);
        return;
      }

      setActivePhotoId(deleteResult.activePhotoId);
    } catch (error) {
      console.error('Failed to delete wedge photo:', error);
      showToast({ message: 'Photo failed to delete.', status: 'error' });
    }
  };

  if (!isOnline) return null;

  return (
    <div className={styles.photoSection}>
      <div className={styles.category}>
        <div className={styles.categoryName}>
          Photos{' '}
          <button type="button" className={styles.photoAction} onClick={handleAddPhoto} aria-label="Add Image">
            + Add Image
          </button>
          {isFetchingImages && (
            <span className={styles.imageLoadingIndicator}>
              <span className={styles.imageLoadingSpinner} aria-hidden="true" />
              Loading images
            </span>
          )}
        </div>
      </div>

      {showPhotoStrip && (
        <PhotoStrip
          photos={visiblePhotos}
          pageIndex={pageIndex}
          pageSize={PhotoHelper.PHOTO_PAGE_SIZE}
          placeholderIconClassName={wedgeStyles.photoPlaceholderIcon}
          totalItems={displayPhotos.length}
          onNextPage={() => setPageIndex((currentPageIndex) => PhotoHelper.getNextPhotoPageIndex(displayPhotos.length, currentPageIndex))}
          onPreviousPage={() => setPageIndex(PhotoHelper.getPreviousPhotoPageIndex)}
          onPhotoClick={openCarousel}
        />
      )}

      <PhotoCarousel isOpen={isCarouselOpen} photos={carouselPhotos} initialPhotoId={activePhotoId} onClose={() => setIsCarouselOpen(false)} onEdit={handleEditPhoto} onDelete={setDeletingPhoto} />

      {deletingPhoto && (
        <DeleteModal title="Delete photo?" body="This photo will be removed from the local list." handleCancel={() => setDeletingPhoto(undefined)} handleConfirm={handleConfirmDelete} />
      )}

      {!isProductionEnv && <PhotoDebugErrorModal error={debugError} onClose={() => setDebugError(undefined)} />}
    </div>
  );
}

export default WedgePhotoCaptureSection;
